import { BoxV2 as Box, Flex } from 'portal-commons';
import { FunctionComponent } from 'react';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { humanize } from '@/utils/string';

/**
 * Props interface for the CampaignAsset component
 * @interface CampaignAssetProps
 * @property {boolean} [disabled] - Whether the asset is disabled
 * @property {('logo'|'banner')} type - The type of campaign asset
 * @property {string} url - The URL of the asset
 * @property {string} name - The name of the asset
 * @property {('verified'|'pending')} [status] - The verification status of the asset
 * @property {('verify'|'edit')} [action] - Determines the action button type:
 *   - 'verify': (default) Shows a verify button for pending assets
 *   - 'edit': Shows an edit button for modifying the asset
 * @property {() => void} [onVerify] - Callback function when verify button is clicked
 * @property {() => void} [onEdit] - Callback function when edit button is clicked
 * @property {{width: string, height: string}} [dimension] - The dimensions of the asset
 */
interface CampaignAssetProps {
  disabled?: boolean;
  type: 'logo' | 'banner';
  url: string;
  name: string;
  status?: 'verified' | 'pending';
  dimension?: { width: string; height: string };
  downloadable?: boolean;
  onDownload?: () => void;
}

const CampaignAsset: FunctionComponent<CampaignAssetProps> = ({
  type,
  url,
  name,
  status,
  dimension,
  downloadable = false,
  onDownload,
}) => {
  const imageDimension = dimension || {
    width: type === 'logo' ? '127px' : '379px',
    height: '127px',
  };
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        width: 'fit-content',
        background: '#F2F3F3',
        border: '1px solid #CCCFD0',
        borderRadius: '4px',
      }}
    >
      <Box
        sx={{
          width: imageDimension.width,
          height: imageDimension.height,
          background: '#FFF',
        }}
      >
        <img
          style={{
            width: '100%',
            height: '100%',
            objectFit: type === 'logo' ? 'contain' : 'cover',
          }}
          src={url}
        />
      </Box>
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '6px',
          padding: `6px 10px ${status ? '19px' : '10px'}`,
          background: '#F2F3F3',
          fontWeight: 400,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            color: '#4D5659',
            fontSize: '14px',
            lineHeight: '16px',
            wordWrap: 'break-word',
            maxWidth: parseInt(imageDimension.width) - 20,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
          title={name}
        >
          {name}
        </Box>
        <Flex sx={{ alignItems: 'center', gap: '4px' }}>
          <Box
            sx={{
              color: status === 'verified' ? '#23B571' : '#D41C62',
              fontSize: '10px',
            }}
          >
            <FontAwesomeIcon icon={faCircle} />
          </Box>
          <Box
            sx={{
              color: '#808789',
              fontSize: '12px',
              lineHeight: '14px',
            }}
          >
            {humanize(status)}
          </Box>
        </Flex>
        {downloadable && (
          <FontAwesomeIcon
            icon={faArrowDownToLine}
            color="#33A7C2"
            fontSize="15"
            style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              cursor: 'pointer',
            }}
            onClick={onDownload}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default CampaignAsset;
